import { useState, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";

const NAV = [
    { to: "/", label: "Home" },
    { to: "/about", label: "About" },
    { to: "/services", label: "Services" },
    { to: "/pricing", label: "Pricing" },
    { to: "/software", label: "Software" },
    { to: "/tax-calculator", label: "Tax Calculator" },
    { to: "/checklists", label: "Checklists" },
    { to: "/structures", label: "Structures" },
    { to: "/faqs", label: "FAQs" },
    { to: "/contact", label: "Contact" },
];

const MAIN_LOGO =
    "/assets/tc-auditors-logo-black.png";

// 079 461 7545 in international (E.164) form for WhatsApp click-to-chat
const WHATSAPP_NUMBER_DISPLAY = "079 461 7545";
const WHATSAPP_URL = "https://wa.me/27794617545";

function WhatsAppIcon({ className = "w-3.5 h-3.5" }) {
    return (
        <svg
            viewBox="0 0 24 24"
            fill="currentColor"
            aria-hidden="true"
            className={className}
        >
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.966-.273-.1-.471-.149-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.173.199-.297.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.876 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
        </svg>
    );
}

export default function Header() {
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    return (
        <header data-testid="site-header" className="sticky top-0 z-50">
            {/* Established 2015 top bar */}
            <div
                data-testid="established-bar"
                className="bg-tca-ink text-white text-[11px] sm:text-xs"
            >
                <div className="container-tca flex items-center justify-between py-2 gap-3">
                    <div className="flex items-center gap-2 truncate">
                        <span className="inline-block w-1.5 h-1.5 bg-tca-orange rounded-full" />
                        <span className="font-semibold tracking-[0.18em] uppercase truncate">
                            Established 2015 — Chartered Accountants &amp; Auditors
                        </span>
                    </div>
                    <a
                        href={WHATSAPP_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`Chat to TC Auditors on WhatsApp at ${WHATSAPP_NUMBER_DISPLAY}`}
                        data-testid="header-whatsapp-link"
                        className="hidden sm:inline-flex items-center gap-2 text-white/80 hover:text-tca-orange transition-colors"
                    >
                        <Phone className="w-3 h-3" />
                        <span>{WHATSAPP_NUMBER_DISPLAY}</span>
                        <WhatsAppIcon className="w-3.5 h-3.5 text-[#25D366]" />
                    </a>
                </div>
            </div>

            {/* Main nav */}
            <div className="bg-white/85 backdrop-blur-xl border-b border-tca-line">
                <div className="max-w-[1440px] mx-auto px-5 sm:px-6 lg:px-8 flex items-center justify-between gap-6 h-[72px]">
                    <Link to="/" data-testid="header-logo-link" className="flex items-center gap-3 flex-shrink-0">
                        <img
                            src={MAIN_LOGO}
                            alt="TC Auditors"
                            className="h-9 sm:h-10 w-auto select-none"
                            loading="eager"
                            decoding="async"
                        />
                    </Link>

                    <nav className="hidden xl:flex items-center gap-5 flex-shrink-0" data-testid="primary-nav">
                        {NAV.map((n) => (
                            <NavLink
                                key={n.to}
                                to={n.to}
                                end={n.to === "/"}
                                data-testid={`nav-link-${n.label.toLowerCase().replace(/\s|&/g, "-")}`}
                                className={({ isActive }) =>
                                    `text-[13px] font-semibold tracking-wide transition-colors relative pb-1 whitespace-nowrap ${
                                        isActive
                                            ? "text-tca-orange after:absolute after:left-0 after:bottom-0 after:h-[2px] after:w-full after:bg-tca-orange"
                                            : "text-tca-ink hover:text-tca-orange"
                                    }`
                                }
                            >
                                {n.label}
                            </NavLink>
                        ))}
                    </nav>

                    <div className="hidden xl:flex items-center gap-3 flex-shrink-0">
                        <Link
                            to="/tax-number"
                            data-testid="header-cta-tax-number"
                            className="btn-primary text-xs px-3 py-2.5 w-28 h-[54px] whitespace-normal text-center leading-tight"
                        >
                            Get My Tax Number
                        </Link>
                        <Link
                            to="/contact"
                            data-testid="header-cta-inquiry"
                            className="btn-primary text-xs px-5 py-2.5 w-28 h-[54px] text-center"
                        >
                            Send Inquiry
                        </Link>
                    </div>

                    <button
                        type="button"
                        aria-label="Toggle menu"
                        data-testid="mobile-menu-toggle"
                        onClick={() => setOpen(!open)}
                        className="xl:hidden p-2 text-tca-ink hover:text-tca-orange transition-colors"
                    >
                        {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                    </button>

                </div>

                {/* Mobile menu */}
                {open && (
                    <div className="xl:hidden border-t border-tca-line bg-white" data-testid="mobile-nav">
                        <div className="container-tca py-4 flex flex-col gap-1">
                            {NAV.map((n) => (
                                <NavLink
                                    key={n.to}
                                    to={n.to}
                                    end={n.to === "/"}
                                    data-testid={`mobile-nav-link-${n.label.toLowerCase().replace(/\s|&/g, "-")}`}
                                    className={({ isActive }) =>
                                        `py-3 px-2 text-sm font-semibold border-b border-tca-line ${
                                            isActive ? "text-tca-orange" : "text-tca-ink"
                                        }`
                                    }
                                >
                                    {n.label}
                                </NavLink>
                            ))}
                            <Link
                                to="/tax-number"
                                data-testid="mobile-cta-tax-number"
                                className="btn-primary mt-3 w-full"
                            >
                                Get My Tax Number
                            </Link>
                            <Link
                                to="/contact"
                                data-testid="mobile-cta-inquiry"
                                className="btn-primary mt-2 w-full"
                            >
                                Send General Inquiry
                            </Link>
                        </div>
                    </div>
                )}
            </div>
        </header>
    );
}
